import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon, CheckIcon } from 'lucide-react';
import type { Product } from '../../data/products';
type ProductCardProps = {
  product: Product;
  reversed?: boolean;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function ProductCard({
  product,
  reversed = false
}: ProductCardProps) {
  const [formatIndex, setFormatIndex] = useState(0);
  const format = product.formats[formatIndex];
  return <article className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-62" data-mp="2i4wgg5o0jq4n">
      <div className={`relative lg:col-span-6 ${reversed ? 'lg:order-2' : ''}`} data-id="element-63" data-mp="2n2fup5kpo2wl">
        <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-white" data-id="element-64" data-mp="2s33kxp148fpr">
          <AnimatePresence mode="wait" initial={false} data-id="element-65" data-mp="2dibu4hiogup">
            <motion.img key={format.image} src={format.image} alt={`${product.name} – ${format.label}`} initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            duration: 0.22,
            ease
          }} className="absolute inset-0 h-full w-full object-contain p-8 md:p-14" data-id="element-66" data-mp="2cyv2r3lryi9" />
          </AnimatePresence>
          <span className="absolute left-5 top-5 rounded-full bg-leaf/15 px-3 py-1.5 text-xs font-semibold text-aloe-700" data-id="element-67" data-mp="2a4nkvr4kgi3m">
            Nouveau
          </span>
        </div>
      </div>

      <div className={`lg:col-span-6 ${reversed ? 'lg:order-1' : ''}`} data-id="element-68" data-mp="20fnh7uf4cyj3">
        <p className="font-serif text-[7rem] leading-[0.8] text-sage md:text-[10rem]" data-id="element-69" data-mp="1vgsre8iqi9ph">
          {product.aloePercent}
          <span className="text-[0.5em] text-leaf" data-id="element-70" data-mp="1rfqy08bi0rvf">%</span>
        </p>
        <p className="mt-2 text-sm font-medium text-ink/60" data-id="element-71" data-mp="1rdebdhusptc1">d’Aloe Vera bio</p>
        <h3 className="mt-8 font-serif text-4xl leading-tight text-aloe-900 md:text-5xl" data-id="element-72" data-mp="1mfct2z571y1m">{product.name}</h3>
        <p className="mt-3 max-w-md text-lg text-ink/70" data-id="element-73" data-mp="1hj4ebmuws6uz">{product.tagline}</p>

        <ul className="mt-8 space-y-3" data-id="element-74" data-mp="17rukhpoigj9x">
          {product.benefits.map(benefit => <li key={benefit} className="flex items-start gap-3 text-ink/80" data-id="element-75" data-mp="1cta6pl2fcvtw">
              <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-leaf" aria-hidden="true" data-id="element-76" data-mp="xw15lznbkdoz" />
              {benefit}
            </li>)}
        </ul>

        <div className="mt-10 flex flex-wrap items-center gap-4" data-id="element-77" data-mp="12w5otxfm3miw">
          {product.formats.length > 1 ? <div role="radiogroup" aria-label="Choisir le format" className="inline-flex rounded-full bg-sand p-1" data-id="element-78" data-mp="1vj066lh4u5s5">
              {product.formats.map((f, i) => <button key={f.label} type="button" role="radio" aria-checked={i === formatIndex} onClick={() => setFormatIndex(i)} className="relative whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium" data-id="element-79" data-mp="1gtu14gvm5lcc" data-mpi={i}>
                  {i === formatIndex && <motion.span layoutId={`format-${product.id}`} transition={{
              duration: 0.22,
              ease
            }} className="absolute inset-0 rounded-full bg-white shadow-sm" data-id="element-80" data-mp="2sxx77t65zam" />}
                  <span className={`relative ${i === formatIndex ? 'text-aloe-900' : 'text-ink/60'}`} data-id="element-81" data-mp="7kz6qlyydbwy">{f.label}</span>
                </button>)}
            </div> : <span className="rounded-full bg-sand px-4 py-2 text-sm font-medium text-aloe-900" data-id="element-82" data-mp="3b2haoibltys3">{format.label}</span>}
          <Link to="/produits/cosmetiques" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-aloe-900 underline decoration-aloe-900/30 underline-offset-4 hover:decoration-aloe-900" data-id="element-83" data-mp="3p2ibxyvs7883">
            Voir la fiche produit
            <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-84" data-mp="10ao66uw0l3wg" />
          </Link>
        </div>
      </div>
    </article>;
}